import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { DateTime } from 'luxon';
import { PtrsInfo } from '../../create-request/models';
import { distributionReasons, stateCodes } from '../utilities/Constants';
import { currencyStringToNumber } from "../../create-request/utilities/currencyStringToNumber";
import { firstCharAlphaNumeric, intermediaryPhoneFormat } from "./regex-patterns";

export const greaterThanZero: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        if (parseFloat(control.value?.replace(/[^0-9\-]/g, '')) <= 0) {
            return { notAPositiveNumber: true };
        }
    }
    return null;
};

export const onlyOneDecimal: ValidatorFn = (control: AbstractControl) => {
    if (control.value?.match(/[.]/g)?.length > 1) {
        return { 'notAPositiveNumber': true };
    }
    return null;
}

export const isANumber: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        if (isNaN(parseFloat(control.value.replace(/[^0-9]/g, '')))) {
            return { notAPositiveNumber: true };
        }
    }
    return null;
};

export const zeroOrGreater: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        if (parseFloat(control.value.replace(/[^0-9\-]/g, '')) < 0) {
            return { notAPositiveNumber: true };
        }
    }
    return null;
};

export const wholeNumber: ValidatorFn = (control: AbstractControl) => {
    if (control.value == 0) {
        return null
    } else if (control.value && control.value.toString().replace('%', '') % 1 !== 0) {
        return { 'notAWholeNumber': true };
    }
    return null;
}

export const lessThanOrEqualTo100: ValidatorFn = (control: AbstractControl) => {
  if (control.value && parseFloat(control.value) > 100.00) {
      return { 'greaterThan100': true };
  }
  return null;
}

export const oneTimeCheckGreaterThan100: ValidatorFn = (control: AbstractControl) => {
  if (control.value && parseFloat(control.value) > 100){
    return { 'oneTimeCheckGreaterThan100': true}
  }
  return null;
}

export const greaterThan99FedRequestCheck: ValidatorFn = (control: AbstractControl) => {
  if (control.value && parseFloat(control.value) > 99.99) {
      return { 'greaterThanOrEqualTo100': true };
  }
  return null;
}

export const oneTimeCheckGreaterThan99State: ValidatorFn = (control: AbstractControl) => {
  if (control.value && parseFloat(control.value) > 99.99) {
    return { 'oneTimeCheckGreaterThan99State': true };
  }
  return null;
}

export const greaterThan99State: ValidatorFn = (control: AbstractControl) => {
  if (control.value && parseFloat(control.value) > 99.99) {
    return { 'greaterThan99State': true};
  }
  return null;
}

export const greaterThan99Fed: ValidatorFn = (control: AbstractControl) => {
    if (control.value && parseFloat(control.value) > 99.99) {
      return { 'greaterThan99Fed': true};
    }
    return null;
  }



export const only2DecimalPlaces: ValidatorFn = (control: AbstractControl) => {
    if (control.value && control.value.split('.')[1] && control.value.split('.')[1].length > 2) {
        return { 'moreThan2DecimalPlaces': true };
    }
    return null;
}

export const lessThan14Digits: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        let numbersBeforeDecimal: string = control.value.split('.')[0];
        if (numbersBeforeDecimal) {
            let rawValue: string = numbersBeforeDecimal.replace('$', '').split(',').join('');
            if (rawValue.length >= 14) {
                return { 'exceeds14Digits': true };
            }
        }
    }
    return null;
}

export const notPastDate: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        const dateTime: DateTime = DateTime.fromFormat(control.value, 'MM/dd/yyyy');
        const date: Date = dateTime.toJSDate();
        const today: Date = new Date(new Date().setHours(0, 0, 0, 0));

        if (date < today) {
            return { 'dateInThePast': true };
        }
    }
    return null;
}

export const futureRMDYear: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        const dateTime: DateTime = DateTime.fromFormat(control.value, 'MM/dd/yyyy');
        const date: Date = dateTime.toJSDate();
        const today: Date = new Date(new Date().setHours(0, 0, 0, 0));

        if (date.getFullYear() < today.getFullYear() + 1) {
            return { 'futureRMDYear': true };
        }
    }
    return null;
}
export const notGreaterThanMaxDate: (maxDate: Date) => ValidatorFn = (maxDate: Date) => {
    return (control: AbstractControl) => {
        if (control.value) {
            const dateTime: DateTime = DateTime.fromFormat(control.value, 'MM/dd/yyyy');
            const date: Date = dateTime.toJSDate();

            if (date > maxDate) {
                return { 'datePastMax': true };
            }
        }
        return null;
    }
}

export const notLessThanMinDate: (minDate: Date) => ValidatorFn = (minDate: Date) => {
    return (control: AbstractControl) => {
        if (control.value) {
            const dateTime: DateTime = DateTime.fromFormat(control.value, 'MM/dd/yyyy');
            const date: Date = dateTime.toJSDate();

            if (date < minDate) {
                return { 'dateLessThanMin': true };
            }
        }
        return null;
    }
}

export const notAWeekend: ValidatorFn = (control: AbstractControl) => {
    if (control.value) {
        const dateTime: DateTime = DateTime.fromFormat(control.value, 'MM/dd/yyyy');
        const date: Date = dateTime.toJSDate();

        if (!(date.getDay() % 6)) {
            return { 'weekend': true };
        }
    }
    return null;
}

export const sumTo100: (
    incomeControlName: string,
    principalControlName: string,
    component: string
) => ValidatorFn = (
    incomeControlName: string,
    principalControlName: string,
    component: string
) => {
        return (control: AbstractControl) => {
            const trustIncomePercent = Number(control.get(incomeControlName)?.value?.replace('%', ''));
            const trustPrincipalPercent = Number(control.get(principalControlName)?.value?.replace('%', ''));

            if (trustIncomePercent + trustPrincipalPercent !== 100) {
                if (component == 'source') {
                    return { 'sourceDoNotSumTo100': true };
                } else if (component == 'receiving') {
                    return { 'receivingDoNotSumTo100': true };
                }
            }
            return null;
        }
    }

export const sumDoesNotExceed99Point99: (
    firstControlName: string,
    secondControlName: string
) => ValidatorFn = (
    firstControlName: string,
    secondControlName: string
) => {
        return (control: AbstractControl) => {
            const firstControl = Number(control.get(firstControlName)?.value?.toString()?.replace('%', ''));
            const secondControl = Number(control.get(secondControlName)?.value?.toString()?.replace('%', ''));

            if (+(firstControl + secondControl).toFixed(4) > 99.99) {
                return { 'sumExceeds99Point99': true };
            }
            return null;
        }
    }

export const requiresStateTaxForEarlyDistribution: ValidatorFn = (control: AbstractControl) => {
    var stateTaxPercentVal = control.get('stateTaxPercent')?.value?.toString()?.replace('%', '')
    if (control.get('distributionReason')?.value === distributionReasons.early && (stateTaxPercentVal === '' || parseInt(stateTaxPercentVal) === 0)) {
        return { 'stateTaxErrorTextMS': true };
    }
    return null;
}

  export const sumDoesNotExceed100: (
    firstControlName: string,
    secondControlName: string
  ) => ValidatorFn = (
    firstControlName: string,
    secondControlName: string
  ) => {
    return (control: AbstractControl) => {
      const firstControl = Number(control.get(firstControlName)?.value?.replace('%',''));
      const secondControl = Number(control.get(secondControlName)?.value?.replace('%',''));

      if (+(firstControl + secondControl).toFixed(4) > 100) {
        return { 'sumExceeds100': true};
      }
      return null;
     }
  }

export const OverdraftRulesValidator: (
    ptrsInfo: PtrsInfo,
    availableCash: number,
    dateControlName: string,
    futureDate: Date,
    incomeControlName: string,
    principalControlName: string
) => ValidatorFn = (
    ptrsInfo: PtrsInfo,
    availableCash: number,
    dateControlName: string,
    futureDate: Date,
    incomeControlName: string,
    principalControlName: string
) => {
        return (control: AbstractControl) => {
            let completionDateString = control.get(dateControlName)?.value ?? '';
            let completionDate = new Date(completionDateString);
            const isFutureDate: boolean = completionDate >= futureDate;
            const trustIncomePercent = +(control.get(incomeControlName)?.value?.replace('%', ''));
            const trustPrincipalPercent = +(control.get(principalControlName)?.value?.replace('%', ''));
            const amount = currencyStringToNumber(control.get('amount')?.value);

            const incomeAmount = trustIncomePercent * amount * 0.01;
            const principalAmount = trustPrincipalPercent * amount * 0.01;

            if (amount > availableCash && !isFutureDate) {
                return { 'insufficientFundError': true };
            } else {
                if (incomeAmount > +ptrsInfo.balances?.income && principalAmount > +ptrsInfo.balances?.principal) {
                    return dualOverdraft(trustIncomePercent, trustPrincipalPercent);
                } else if (incomeAmount > +ptrsInfo.balances?.income) {
                    return incomeOverdraft(trustIncomePercent);
                } else if (principalAmount > +ptrsInfo.balances?.principal) {
                    return principalOverdraft(trustPrincipalPercent);
                }
            }
            return null;
        }

        function incomeOverdraft(trustIncomePercent: number): ValidationErrors | null {
            if (!ptrsInfo.overdraftRules.allowIncomeOverdraft && !ptrsInfo.overdraftRules.allowManualOverride && trustIncomePercent !== 0) {
                return { 'incomePercentError': true };
            } else if (
                !ptrsInfo.overdraftRules.allowIncomeOverdraft &&
                ptrsInfo.overdraftRules.allowManualOverride
            ) {
                return { 'overridePopupIncomeError': true }
            }
            return null;
        }

        function principalOverdraft(trustPrincipalPercent: number): ValidationErrors | null {
            if (!ptrsInfo.overdraftRules.allowPrincipalOverdraft && !ptrsInfo.overdraftRules.allowManualOverride && trustPrincipalPercent !== 0) {
                return { 'principalPercentError': true }
            } else if (
                !ptrsInfo.overdraftRules.allowPrincipalOverdraft &&
                ptrsInfo.overdraftRules.allowManualOverride
            ) {
                return { 'overridePopupPrincipalError': true }
            }
            return null;
        }

        function dualOverdraft(trustIncomePercent: number, trustPrincipalPercent: number): ValidationErrors | null {
            if (
                !ptrsInfo.overdraftRules.allowIncomeOverdraft &&
                !ptrsInfo.overdraftRules.allowPrincipalOverdraft &&
                !ptrsInfo.overdraftRules.allowManualOverride
            ) {
                return { 'incomeAndPrincipalPercentError': true }
            } else if (
                !ptrsInfo.overdraftRules.allowIncomeOverdraft &&
                !ptrsInfo.overdraftRules.allowPrincipalOverdraft &&
                ptrsInfo.overdraftRules.allowManualOverride
            ) {
                return { 'overridePopupIncomeAndPrincipalError': true }
            } else if (ptrsInfo.overdraftRules.allowIncomeOverdraft && !ptrsInfo.overdraftRules.allowPrincipalOverdraft) {
                return principalOverdraft(trustPrincipalPercent);
            } else if (!ptrsInfo.overdraftRules.allowIncomeOverdraft && ptrsInfo.overdraftRules.allowPrincipalOverdraft) {
                return incomeOverdraft(trustIncomePercent);
            }
            return null;
        }
    }

export const firstCharacterAlphaNumeric: ValidatorFn = (
    control: AbstractControl
) => {
    if (control.value && !firstCharAlphaNumeric.test(control.value)) {
        return { firstCharAlphaNumeric: true };
    }
    return null;
};

export const phoneNumberWithPossibleExtension: ValidatorFn = (control: AbstractControl) => {
    if (control.value && !intermediaryPhoneFormat.test(control.value)) {
        return { invalidPhoneFormat: true }
    }

    return null;
}

export const isEmptyString: ValidatorFn = (control: AbstractControl) => {
    if (control.value && typeof control.value === 'string'){
        if (control.value.trim().length == 0){
            return { required : true };
        }
    }
    return null;
}

export const belowMIStateTax: (
  control: AbstractControl,
  stateCode: string,
  defaultRate: string
) => ValidatorFn = (
  control: AbstractControl,
  stateCode: string,
  defaultRate: string,
) => {
  return (control: AbstractControl) => {
    let enteredRate = +control.value.replace('%', '');
    if (stateCode !== stateCodes.MICHIGAN || enteredRate === Number.parseFloat(defaultRate)) {
      return null;
    }
    if (enteredRate < 4.25) {
      return {'belowStateTax': true}
    }
    return null;
  }
}
export const belowMNStateTax: (
  control: AbstractControl,
  stateCode: string,
  defaultRate: string
) => ValidatorFn = (
  control: AbstractControl,
  stateCode: string,
  defaultRate: string
) => {
  return (control: AbstractControl) => {
    let enteredRate = +control.value.replace('%', '');
    if (stateCode !== stateCodes.MINNESOTA || enteredRate === Number.parseFloat(defaultRate)) {
      return null;
    }
    if (enteredRate < 6.25) {
      return {'belowStateTax': true}
    }
    return null;
  }
}


export const MustSelectQCDToProceed: ValidatorFn = (control: AbstractControl) => {
  if (control.value) {
    if (control.value === distributionReasons.qcd) {
      return null;
    } else {
      return {'qcdReasonRequired': true}
    }
  }
  return null;
}

export const requiresStateTaxForEarlyDistributionForInterTransfers = (isEarlyDistributionReason: boolean): ValidatorFn => {
    return (control: AbstractControl) => {
        var stateTaxPercentVal = control.get('stateTaxPercent')?.value?.toString()?.replace('%', '');
        if (isEarlyDistributionReason && (stateTaxPercentVal === '' || parseInt(stateTaxPercentVal) === 0)) {
            return { 'stateTaxErrorTextMS': true };
        }
        return null;
    };
}

